import React, { useState } from 'react';

import { ButtonBase, DialogContent, Menu, MenuItem, Typography } from '@mui/material';
import { PlusCircle } from 'lucide-react';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import {
  AlineDialog,
  AlineDialogButtons,
  AlineTooltip,
  DocumentPicker,
  NameDialog,
  NameDialogVariant,
} from 'src/components/generic';
import { AddIcon } from 'src/components/icons/AddIcon';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { TemplateIcon } from 'src/components/icons/TemplateIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { FileStackDomContainer } from 'src/helpers';
import { useCreateDocument } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { DocumentChipById } from './board/workflow-card/components/WorkflowFileChips';

const MAX_VISIBLE_CHIPS = 3;
const ATTACH_FILES_LABEL = 'Attach Files';

const useStyles = makeStyles()(theme => ({
  attachFilesButton: {
    '&:hover': {
      borderColor: theme.palette.primary.main,
      cursor: 'pointer',
    },
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: '2px dashed #D0D5DD',
    borderRadius: 8,
    boxSizing: 'border-box',
    color: theme.palette.primary.main,
    display: 'flex',
    gap: theme.spacing(0.75),
    height: 32,
    padding: theme.spacing(0.5, 2),
    transition: 'border-color 0.2s ease-in-out',
  },
  dialogContainer: {
    maxWidth: 1080,
    minWidth: 1080,
  },
  dialogContent: {
    boxSizing: 'border-box',
    flexDirection: 'column',
    width: '100%',
  },
  menuIcon: {
    marginRight: theme.spacing(1),
  },
  moreChip: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[100],
    borderRadius: 16,
    color: theme.palette.text.secondary,
    cursor: 'default',
    display: 'flex',
    fontSize: 12,
    fontWeight: 500,
    padding: theme.spacing(0.75, 1.5),
  },
}));

export interface SelectedDocument {
  id: string;
}

interface SelectedDocumentChipsProps {
  documents: SelectedDocument[];
  onRemove: (id: string) => void;
}

/**
 * Displays selected document chips with a "+X more" indicator when exceeding MAX_VISIBLE_CHIPS.
 */
export function SelectedDocumentChips({
  documents,
  onRemove,
}: SelectedDocumentChipsProps): JSX.Element | null {
  const { classes } = useStyles();

  if (documents.length === 0) return null;

  const visibleDocs = documents.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = documents.length - MAX_VISIBLE_CHIPS;

  return (
    <>
      {visibleDocs.map(doc => (
        <DocumentChipById key={doc.id} documentId={doc.id} onDelete={() => onRemove(doc.id)} />
      ))}
      {hiddenCount > 0 && <span className={classes.moreChip}>+{hiddenCount} more</span>}
    </>
  );
}

interface DocumentPickerPopoverProps {
  /** Callback when documents are added */
  onDocumentsChange: (documents: SelectedDocument[]) => void;
  /** Currently selected document IDs */
  selectedDocuments: SelectedDocument[];
}

/**
 * A popover for selecting documents from repository or uploading new ones.
 * Used in workflow creation where we don't have a workflow ID yet.
 */
export function DocumentPickerPopover({
  onDocumentsChange,
  selectedDocuments,
}: DocumentPickerPopoverProps): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const { createDocument } = useCreateDocument();
  const [menuOpen, setMenuOpen] = useState(false);
  const [repoPickerOpen, setRepoPickerOpen] = useState(false);
  const [isFilePickerOpen, setFilePickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingDocumentIds, setPendingDocumentIds] = useState<string[]>([]);
  const [createDocumentDialogOpen, setCreateDocumentDialogOpen] = useState(false);
  const [repoPickerVariant, setRepoPickerVariant] = useState<Apollo.DocumentVariant>(
    Apollo.DocumentVariant.Document
  );
  // Once chips fill the row the button drops its label, so the tooltip carries the meaning instead.
  const hasSelectedDocuments = selectedDocuments.length > 0;

  const closeRepoPicker = () => {
    setRepoPickerOpen(false);
    setPendingDocumentIds([]);
  };

  const openRepoPicker = (variant: Apollo.DocumentVariant) => {
    setMenuOpen(false);
    setRepoPickerVariant(variant);
    setRepoPickerOpen(true);
  };

  return (
    <>
      <AlineTooltip arrow placement="top" title={hasSelectedDocuments ? ATTACH_FILES_LABEL : ''}>
        <ButtonBase
          ref={setAnchorEl}
          aria-label={ATTACH_FILES_LABEL}
          className={classes.attachFilesButton}
          onClick={() => setMenuOpen(true)}
        >
          <PlusCircle size={16} />
          {!hasSelectedDocuments && <Typography variant="body2">{ATTACH_FILES_LABEL}</Typography>}
        </ButtonBase>
      </AlineTooltip>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={menuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setMenuOpen(false)}
      >
        <MenuItem
          onClick={() => {
            setMenuOpen(false);
            setCreateDocumentDialogOpen(true);
          }}
        >
          <AddIcon className={classes.menuIcon} />
          <Typography>Create blank document</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuOpen(false);
            setFilePickerOpen(true);
          }}
        >
          <UploadIcon className={classes.menuIcon} />
          <Typography>Upload files</Typography>
        </MenuItem>
        <MenuItem onClick={() => openRepoPicker(Apollo.DocumentVariant.Document)}>
          <DocumentIcon className={classes.menuIcon} />
          <Typography>Pick from repository</Typography>
        </MenuItem>
        <MenuItem onClick={() => openRepoPicker(Apollo.DocumentVariant.Template)}>
          <TemplateIcon className={classes.menuIcon} />
          <Typography>Use template</Typography>
        </MenuItem>
      </Menu>

      {/* Upload Files Picker */}
      <UploadFilesPicker
        domContainer={FileStackDomContainer.SendForSignaturePage}
        open={isFilePickerOpen}
        pickerOpts={{
          afterUploadDone: async res => {
            setFilePickerOpen(false);
            const uploadedFiles = res.data?.createFiles ?? [];
            if (uploadedFiles.length) {
              const newDocs = uploadedFiles.map(file => ({ id: file.id }));
              onDocumentsChange([...selectedDocuments, ...newDocs]);
            }
          },
          onClose: () => setFilePickerOpen(false),
          props: {
            documentVariant: Apollo.DocumentVariant.Document,
            isSigned: false,
          },
        }}
      />

      {/* Repository Picker Dialog */}
      {currentUser?.myRepositoryId && (
        <AlineDialog
          classes={{ paperWidthMd: classes.dialogContainer }}
          open={repoPickerOpen}
          title={
            repoPickerVariant === Apollo.DocumentVariant.Document
              ? 'Add Documents'
              : 'Add Templates'
          }
          onClose={closeRepoPicker}
        >
          <DialogContent className={classes.dialogContent}>
            <DocumentPicker
              documentVariant={repoPickerVariant}
              getIsRowDisabled={document => {
                const isAlreadySelected = selectedDocuments.some(d => d.id === document.id);
                if (isAlreadySelected) {
                  return {
                    isRowDisabled: true,
                    reason: 'Document already selected',
                  };
                }
                return { isRowDisabled: false, reason: '' };
              }}
              repositoryId={currentUser.myRepositoryId}
              onSelectionChange={ids => setPendingDocumentIds(ids)}
            />
          </DialogContent>
          <AlineDialogButtons
            cancelHandler={closeRepoPicker}
            submitHandler={async () => {
              if (pendingDocumentIds.length === 0) return closeRepoPicker();
              const newDocs = pendingDocumentIds.map(id => ({ id }));
              onDocumentsChange([...selectedDocuments, ...newDocs]);
              closeRepoPicker();
            }}
            submitText={
              pendingDocumentIds.length ? `Add ${pendingDocumentIds.length} files` : 'Done'
            }
          />
        </AlineDialog>
      )}

      {/* Create Blank Document Dialog */}
      <NameDialog
        handleClose={() => setCreateDocumentDialogOpen(false)}
        open={createDocumentDialogOpen}
        placeholder="Add a title for your document..."
        variant={NameDialogVariant.CreateDocument}
        onFormikSubmit={async name => {
          const { data } = await createDocument({
            variables: {
              documentProps: {
                name,
                variant: Apollo.DocumentVariant.Document,
              },
            },
          });
          if (data?.createDocument.document) {
            onDocumentsChange([...selectedDocuments, { id: data.createDocument.document.id }]);
          }
          setCreateDocumentDialogOpen(false);
        }}
      />
    </>
  );
}
